'use client';

import { useEffect, useMemo, useState } from 'react';
import {
  ArrowRight,
  Bookmark,
  BookmarkCheck,
  Building2,
  CheckCircle2,
  ExternalLink,
  Eye,
  FileText,
  Info,
  Linkedin,
  Loader2,
  Lock,
  Mail,
  MapPin,
  MoreVertical,
  PenLine,
  RotateCcw,
  Sparkles,
  UserSearch,
  Users,
  XCircle,
  Zap,
} from 'lucide-react';
import MainLoader from '@/components/ui/feedback/MainLoader';
import { OutreachAgentProgress } from '@/app/dashboard/components/outreach/OutreachAgentProgress';
import { JobDescriptionPanel } from '@/components/features/jobs/JobDescriptionPanel';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/navigation/dropdown-menu';
import { InfoPopover } from '@/components/ui/feedback/InfoPopover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
} from '@/components/ui/forms/select';
import { cn } from '@/lib/utils';
import type { ContactLibraryState } from '@/components/ui/design-system/entities/contact';
import type { CardDecision, DeckCardView, DeckGroup, MessageState, SaveState } from '../types';
import { buildCardView, jobLocationLabel } from '../hooks/useSimpleSearchDeck';
import { useEmailValidations } from '@/app/dashboard/components/outreach/hooks/useEmailValidations';
import {
  getRecipientStatusUi,
  getRecipientValidationStatus,
  isLinkedInRecipient,
  orderRecipientOptions,
} from '@/app/dashboard/components/outreach/actions/RecipientEmailSelect';
import { normalizeEmailForValidation } from '@/lib/contact-email-validation';
import { CompanyMonogram } from './CompanyMonogram';
import { Badge, type BadgeProps } from '@/components/ui/data-display/badge';
import { maskRecipientEmail } from '@/lib/outreach/recipient-privacy';

/**
 * Derive a readable slug from a LinkedIn URL, e.g. "https://linkedin.com/in/ying-zhou"
 * -> "ying-zhou". Mirrors ReplyComposer's local `deriveLinkedInSlug` so the same
 * profile reads identically in both the composer and this card's "To" dropdown.
 * Falls back to the raw URL when it can't be parsed.
 */
function deriveLinkedInSlug(url: string): string {
  const raw = typeof url === 'string' ? url.trim() : '';
  if (!raw) {
    return '';
  }
  try {
    const parsed = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
    const segments = parsed.pathname.split('/').filter(Boolean);
    const inIndex = segments.indexOf('in');
    if (inIndex >= 0 && segments[inIndex + 1]) {
      return segments[inIndex + 1];
    }
    if (segments.length > 0) {
      return segments[segments.length - 1];
    }
    return raw;
  } catch {
    return raw;
  }
}

/**
 * Locked recipient row shown to UNPAID users in place of the editable recipient
 * select: the address is masked and clicking "Reveal email" runs the paywall
 * guard (reveal on success, paywall otherwise).
 *
 * The server already redacts the address to its masked form for unpaid users,
 * so `options` here carries no raw email — the client mask is a defensive second
 * pass (idempotent). NO deliverability badge is shown: an unpaid user can't use
 * the address, so validating it or surfacing a status signal is pointless (and
 * would leak deliverability info). Validation is skipped entirely upstream.
 */
function RecipientEmailReveal({
  options,
  onReveal,
}: {
  options: string[];
  onReveal?: () => void;
}) {
  const primary = options[0] ?? '';
  return (
    <div className="flex min-w-0 items-center gap-1.5">
      <button
        type="button"
        onClick={onReveal}
        aria-label="Reveal recipient email — upgrade to send outreach"
        className="inline-flex min-w-0 items-center gap-1 text-body-sm text-primary underline decoration-dotted transition-colors hover:text-primary/80"
      >
        <Lock className="h-3 w-3 shrink-0" />
        <span className="min-w-0 truncate">{maskRecipientEmail(primary)}</span>
        <span className="shrink-0 font-medium">· Reveal email</span>
      </button>
    </div>
  );
}

const DECISION_BADGE: Record<CardDecision, { label: string; variant: BadgeProps['variant'] } | undefined> = {
  sent: { label: 'Sent', variant: 'default' },
  draft: { label: 'Saved', variant: 'secondary' },
  discarded: { label: 'Passed', variant: 'secondary' },
  skipped: { label: 'Passed', variant: 'secondary' },
  applied: { label: 'Applied', variant: 'status-applied' },
  saved: { label: 'Saved', variant: 'secondary' },
};

/**
 * Props shared by the two content slots of a Simple Search card. The card's
 * pile mechanics (stacking, drag, stamps, fly-out) live in the generic
 * `SwipePile`; these components only render content.
 */
export interface SwipeCardContentProps {
  group: DeckGroup;
  /** Which contact in the group is on top. */
  activeContactIndex: number;
  paywallMode: boolean;
  getMessage: (msgKey: string) => MessageState;
  getDecision: (key: string) => CardDecision | undefined;
  getLibraryState: (key: string) => ContactLibraryState | undefined;
  onPaywallTrigger: () => void;
  onRetryMessage: (view: DeckCardView) => void;
  /**
   * Rebuild the current card's draft (bypasses + overwrites the cached draft).
   * Backs the "Rebuild" button in the Generated Outreach header. Optional — when
   * omitted the button isn't shown.
   */
  onRefreshMessage?: (view: DeckCardView) => void;
  /**
   * Preview-only (past-decision cards). When provided and the review message is
   * ready-but-empty, the Generated Outreach panel shows a centered "Generate
   * outreach" button instead of an empty composer. Omitted on the live deck,
   * where messages are pre-generated, so the placeholder never shows there.
   */
  onGenerate?: (view: DeckCardView) => void;
  onSave: (view: DeckCardView) => void;
  onEdit: (view: DeckCardView) => void;
  onViewCompany: (view: DeckCardView) => void;
  onViewJob: (view: DeckCardView) => void;
  onViewProfile: (view: DeckCardView) => void;
  onSkipCompany: () => void;
  onSkipJob: () => void;
  /** Job-only card: open the posting's apply URL. */
  onApply: (view: DeckCardView) => void;
  /** Job-only card: auto-submit the application via SimpleApply (1-Click Apply). */
  onSuperApply: (view: DeckCardView) => void;
  superApplyBusy?: boolean;
  /** Job-only card: look up a hiring-manager contact on demand. */
  onContactHiringManager: (view: DeckCardView) => void;
  contactLookupBusy?: boolean;
  onSaveCompany: (view: DeckCardView) => void;
  onSaveJob: (view: DeckCardView) => void;
  companySaveState?: SaveState;
  jobSaveState?: SaveState;
  /**
   * Message-block presentation. 'default' is the compact Simple Search preview
   * (subject line + body, edit pencil). 'review' is the Automations approval
   * layout: a "Generated outreach" label, explicit Subject / Message structure,
   * and quality chips — so the draft reads like a document to approve, not a
   * textarea. Defaults to 'default' so Simple Search is untouched.
   */
  messageVariant?: 'default' | 'review';
  /**
   * Whether the recipient email in the 'review' "To" row is revealed. Paid users
   * (or once revealed via the paywall) see the editable recipient select; unpaid
   * users see a masked address behind a "Reveal email" gate. Defaults to true so
   * Simple Search and read-only previews are unaffected.
   */
  revealRecipient?: boolean;
  /** Clicked "Reveal email" on a locked recipient row (runs the paywall guard). */
  onRevealRecipient?: () => void;
  /**
   * Show the company view/save/skip controls even in the 'review' variant. The
   * standalone approval queue (ReviewQueue) hides them because its cards carry
   * only a minimal contact + company name (the controls would be dead); the Home
   * outreach queue runs Simple Search under the hood, so its cards have real
   * company data and opt in. Defaults to false.
   */
  enableEntityActions?: boolean;
  /**
   * Show the in-card "1-Click Apply" button on job-only cards. Defaults to true
   * (Simple Search). The Home outreach queue sets this false — there 1-Click Apply
   * is the footer's primary action (next to Pass), so the in-card copy is redundant.
   */
  showCardSuperApply?: boolean;
  /**
   * Tailored resume state for the current card's job. When provided, an "Attached
   * resume" section renders below the message body showing the resume name (if
   * tailored) or a "Tailor resume" button (if not). Omit to hide the section.
   */
  tailoredResume?: { resumeId: string; resumeName: string } | null;
  /** Whether a tailoring operation is currently in progress for this card. */
  tailoringInProgress?: boolean;
  /** Triggered when the user clicks "Tailor resume" on the card. */
  onTailorResume?: (view: DeckCardView) => void;
}

/**
 * Header section of a Simple Search card — company, active role, and contact
 * progress. Rendered together with SwipeCardBody as one whole-card unit that
 * swipes; each contact is its own card, so the company repeats per card.
 */
export function SwipeCardHeader({
  group,
  activeContactIndex,
  onViewCompany,
  onViewJob,
  onViewProfile: _onViewProfile,
  onSkipCompany,
  onSkipJob,
  onSaveCompany,
  onSaveJob,
  companySaveState,
  jobSaveState,
  onContactHiringManager,
  contactLookupBusy,
  messageVariant = 'default',
  enableEntityActions = false,
}: SwipeCardContentProps) {
  const view = buildCardView(group, activeContactIndex);
  // Approval-queue context (Automations) carries only a company name and a
  // minimal contact — no data to power a quick-view or a save — so the company
  // utility buttons would be dead. Show the company as read-only context there,
  // unless the caller opts into the entity controls (Home runs Simple Search
  // under the hood and has real company data behind these buttons).
  const isReview = messageVariant === 'review';
  const showEntityControls = !isReview || enableEntityActions;
  const isJobCard = view?.kind === 'job';
  const contact = view?.contact;
  const companyName = group.company?.name ?? contact?.company?.name ?? '';
  const description = group.company?.description;
  const job = group.job;
  const jobLocation = jobLocationLabel(job);
  const contactCount = group.contacts.length;

  return (
    <div className="p-4 pb-0">
      <div className="flex items-start gap-3">
        <CompanyMonogram logoUrl={group.company?.logoUrl} name={companyName || 'Company'} size="md" resolveLogo />
        <div className="min-w-0 flex-1">
          {job ? (
            // Job present: lead with the ROLE, then the company (smaller) + location,
            // stacked tight (no dead space). Company shrinks only in this case.
            <>
              {/* Role on top — underlined link that opens the job dialog. */}
              {view ? (
                <button
                  type="button"
                  onClick={() => onViewJob(view)}
                  title="View job"
                  className="block max-w-full truncate text-left text-title-sm text-foreground underline underline-offset-2 transition-colors hover:text-primary"
                >
                  {job.title}
                </button>
              ) : (
                <p className="truncate text-title-sm text-foreground">{job.title}</p>
              )}
              {/* Company (icon + link → company dialog) + location on ONE line to save space. */}
              {companyName || jobLocation ? (
                <div className="mt-1.5 flex min-w-0 items-center gap-1.5 text-body-sm text-muted-foreground">
                  {companyName ? (
                    <span className="flex min-w-0 items-center gap-1">
                      <Building2 className="h-3.5 w-3.5 shrink-0" />
                      {showEntityControls && group.company && view ? (
                        <button
                          type="button"
                          onClick={() => onViewCompany(view)}
                          title="View company"
                          className="min-w-0 truncate text-left font-medium underline underline-offset-2 transition-colors hover:text-foreground"
                        >
                          {companyName}
                        </button>
                      ) : (
                        <span className="min-w-0 truncate font-medium">{companyName}</span>
                      )}
                    </span>
                  ) : null}
                  {jobLocation ? (
                    <span className="flex shrink-0 items-center gap-1">
                      {companyName ? (
                        <span aria-hidden="true" className="text-muted-foreground/50">·</span>
                      ) : null}
                      <MapPin className="h-3.5 w-3.5 shrink-0" />
                      <span className="shrink-0">{jobLocation}</span>
                    </span>
                  ) : null}
                </div>
              ) : null}
            </>
          ) : (
            // No job: company stays primary (big), an underlined link + its description.
            <>
              {companyName ? (
                showEntityControls && group.company && view ? (
                  <button
                    type="button"
                    onClick={() => onViewCompany(view)}
                    title="View company"
                    className="block max-w-full truncate text-left text-title-sm text-foreground underline underline-offset-2 transition-colors hover:text-primary"
                  >
                    {companyName}
                  </button>
                ) : (
                  <p className="truncate text-title-sm text-foreground">{companyName}</p>
                )
              ) : (
                <p className="truncate text-title-sm text-muted-foreground">Company</p>
              )}
              {description ? (
                <p className="mt-0.5 line-clamp-2 text-body-sm text-muted-foreground">{description}</p>
              ) : group.companyResolution === 'pending' ? (
                <p className="mt-0.5 text-body-sm text-muted-foreground">Loading company…</p>
              ) : null}
            </>
          )}
        </div>
        {showEntityControls && view && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Company & job actions"
                title="More"
                className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <MoreVertical className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {group.company && view ? (
                <DropdownMenuItem onSelect={() => onViewCompany(view)}>
                  <Eye />
                  View company
                </DropdownMenuItem>
              ) : null}
              {companyName ? (
                <DropdownMenuItem
                  disabled={companySaveState?.saved || companySaveState?.saving}
                  onSelect={() => onSaveCompany(view)}
                >
                  <Bookmark />
                  {companySaveState?.saved ? 'Company saved' : 'Save company'}
                </DropdownMenuItem>
              ) : null}
              {group.company ? (
                <DropdownMenuItem onSelect={onSkipCompany}>
                  <ArrowRight />
                  Skip company
                </DropdownMenuItem>
              ) : null}
              {job ? (
                <DropdownMenuItem
                  disabled={jobSaveState?.saved || jobSaveState?.saving}
                  onSelect={() => onSaveJob(view)}
                >
                  <Bookmark />
                  {jobSaveState?.saved ? 'Job saved' : 'Save job'}
                </DropdownMenuItem>
              ) : null}
              {job ? (
                <DropdownMenuItem onSelect={onSkipJob}>
                  <ArrowRight />
                  Skip job
                </DropdownMenuItem>
              ) : null}
              {isJobCard && view ? (
                <DropdownMenuItem
                  disabled={Boolean(contactLookupBusy)}
                  onSelect={() => onContactHiringManager(view)}
                >
                  <UserSearch />
                  {contactLookupBusy ? 'Finding hiring manager…' : 'Contact Hiring Manager'}
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Contact progress within this company/role. */}
      {view && contactCount > 0 && (
        <div className="mt-3 flex items-center gap-2">
          <Users className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="shrink-0 text-caption font-medium text-foreground">
            {contactCount > 1
              ? `${activeContactIndex + 1} of ${contactCount} contacts`
              : '1 contact'}
          </span>
          {contactCount > 1 && (
            <div className="flex min-w-0 flex-1 items-center gap-1">
              {group.contacts.map((c, i) => (
                <span
                  key={c.id ?? c.email ?? i}
                  className={cn(
                    'h-1 flex-1 rounded-full transition-colors',
                    i <= activeContactIndex ? 'bg-primary' : 'bg-border',
                  )}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mt-3 h-px w-full bg-border" />
    </div>
  );
}

/**
 * Swipeable body of a Simple Search card — the job actions (no contact found)
 * or the contact identity + AI-drafted message. Rendered inside SwipePile's
 * draggable region.
 */
export function SwipeCardBody({
  group,
  activeContactIndex,
  paywallMode,
  getMessage,
  getDecision,
  getLibraryState,
  onPaywallTrigger,
  onRetryMessage,
  onRefreshMessage,
  onGenerate,
  onSave,
  onEdit,
  onViewProfile,
  onViewJob,
  onApply,
  onSuperApply,
  superApplyBusy,
  onContactHiringManager,
  contactLookupBusy,
  messageVariant = 'default',
  revealRecipient = true,
  onRevealRecipient,
  showCardSuperApply = true,
  enableEntityActions = false,
  tailoredResume,
  tailoringInProgress,
  onTailorResume,
}: SwipeCardContentProps) {
  const view = buildCardView(group, activeContactIndex);
  const isJobCard = view?.kind === 'job';
  const isReview = messageVariant === 'review';
  const contact = view?.contact;
  const message = view ? getMessage(view.msgKey) : { status: 'idle' as const };
  // Past-decision cards carry no persisted draft, so a "ready" message with no body
  // is the cue to offer on-demand generation (only where a handler is wired).
  const showGeneratePlaceholder =
    isReview && message.status === 'ready' && !message.body && Boolean(onGenerate);
  const decision = view ? getDecision(view.key) : undefined;
  const libraryState = view ? getLibraryState(view.key) : undefined;
  const job = group.job;
  const titleLine = contact
    ? [contact.title, contact.location].filter(Boolean).join(' · ')
    : '';
  const saveInLibrary = Boolean(libraryState?.isInLibrary);
  const saveAdding = Boolean(libraryState?.isAdding);

  // Recipient deliverability — fetch validation for the drafted card's contact
  // so the review preview shows the same "checking / deliverable / issue" status
  // as the full composer. Only runs for review cards with a ready draft AND a
  // revealed (paid) recipient: unpaid users can't see or use the address, so
  // validating it (and showing a status badge) is pointless and leaks signal —
  // the server also sends them only the masked address, so there's nothing real
  // to validate. Paid users are unaffected.
  const recipientEmailOptions = useMemo(
    () => (contact ? [contact.email, ...(contact.emails ?? [])] : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contact?.id, contact?.email, (contact?.emails ?? []).join(',')],
  );
  // LinkedIn goes first, ahead of email — mirrors how the full outreach
  // composer (ConnectedOutreachDialog / ReplyComposer) treats LinkedIn as a
  // channel option. Kept separate from `recipientEmailOptions`: that list also
  // feeds the unpaid `RecipientEmailReveal` row below, which only knows how to
  // mask email addresses — LinkedIn was never the thing paywalled behind
  // "Reveal email", so it must not flow into that path.
  const recipientOptions = useMemo(
    () =>
      contact?.linkedInUrl ? [contact.linkedInUrl, ...recipientEmailOptions] : recipientEmailOptions,
    [contact?.linkedInUrl, recipientEmailOptions],
  );
  const { validations: recipientValidations } = useEmailValidations(
    recipientEmailOptions,
    contact?.id,
    isReview && message.status === 'ready' && Boolean(contact) && revealRecipient,
  );
  const [selectedRecipient, setSelectedRecipient] = useState<string>('');
  // Reset the picked recipient when the card changes so a stale address from a
  // previous contact never leaks into the next one.
  useEffect(() => {
    setSelectedRecipient('');
  }, [contact?.id]);

  // Recipient "To" dropdown — same Select-based UI ReplyComposer uses: LinkedIn
  // pinned first behind a separator, each email item carrying its own
  // deliverability badge, and a trailing status pill next to the trigger.
  const validRecipientOptions = useMemo(
    () => recipientOptions.filter((e): e is string => typeof e === 'string' && e.trim().length > 0),
    [recipientOptions],
  );
  const linkedInRecipientValue = useMemo(
    () => validRecipientOptions.find((option) => isLinkedInRecipient(option)),
    [validRecipientOptions],
  );
  const linkedInSlug = useMemo(
    () => (linkedInRecipientValue ? deriveLinkedInSlug(linkedInRecipientValue) : ''),
    [linkedInRecipientValue],
  );
  const orderedEmailRecipientOptions = useMemo(() => {
    const emailOptions = validRecipientOptions.filter((option) => !isLinkedInRecipient(option));
    return orderRecipientOptions(emailOptions, recipientValidations);
  }, [validRecipientOptions, recipientValidations]);
  const combinedRecipientOptions = useMemo(
    () =>
      linkedInRecipientValue ? [linkedInRecipientValue, ...orderedEmailRecipientOptions] : orderedEmailRecipientOptions,
    [linkedInRecipientValue, orderedEmailRecipientOptions],
  );
  const resolvedSelectedRecipient =
    selectedRecipient.trim() ||
    combinedRecipientOptions.find((option) => option.trim().length > 0) ||
    '';
  const isSelectedRecipientLinkedIn = isLinkedInRecipient(resolvedSelectedRecipient);
  const selectedRecipientValidationStatus = useMemo(
    () => getRecipientValidationStatus(resolvedSelectedRecipient, recipientValidations),
    [resolvedSelectedRecipient, recipientValidations],
  );
  const selectedRecipientValidationDetail = useMemo(() => {
    const normalized = normalizeEmailForValidation(resolvedSelectedRecipient);
    if (!normalized) {
      return null;
    }
    const detail = recipientValidations?.[normalized]?.detail;
    return typeof detail === 'string' && detail.trim().length > 0 ? detail.trim() : null;
  }, [recipientValidations, resolvedSelectedRecipient]);
  const selectedRecipientStatusUi = useMemo(
    () => getRecipientStatusUi(selectedRecipientValidationStatus),
    [selectedRecipientValidationStatus],
  );
  const selectedRecipientStatusDescription = selectedRecipientValidationDetail
    ? `${selectedRecipientStatusUi.description} ${selectedRecipientValidationDetail}`
    : selectedRecipientStatusUi.description;

  if (isJobCard && view) {
    return (
      <div
        key={view.key}
        className="flex min-h-0 flex-1 flex-col gap-3 px-4 pb-4 pt-3 animate-[slideUpFade_0.25s_ease-out]"
      >
        {/* Role description — rendered exactly like the job-details page
            (rich HTML, entities decoded), clamped to the card with a fade. */}
        {job?.description ? (
          <JobDescriptionPanel
            html={job.description}
            variant="preview"
            onShowFull={() => onViewJob(view)}
          />
        ) : (
          <div className="flex-1" />
        )}

        {/* Action block, pinned to the bottom. */}
        <div className="shrink-0 space-y-2.5">
          <div className="flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 text-body-sm text-muted-foreground">
            <span>No contact found</span>
            <InfoPopover
              ariaLabel="Why no contact?"
              align="center"
              triggerClassName="h-5 w-5"
              content="We couldn't surface a contact here — let 1-Click Apply submit it for you, or reach out directly."
            />
            <span aria-hidden="true" className="text-muted-foreground/50">·</span>
            <button
              type="button"
              onClick={() => onApply(view)}
              className="inline-flex items-center gap-1 font-medium underline underline-offset-2 transition-colors hover:text-foreground"
            >
              Apply normally
              <ExternalLink className="h-3.5 w-3.5 no-underline" />
            </button>
          </div>
          {showCardSuperApply ? (
            <button
              type="button"
              onClick={() => onSuperApply(view)}
              disabled={Boolean(superApplyBusy)}
              aria-label="1-Click Apply — auto-submit this application"
              className="flex w-full items-center justify-center gap-2 border border-primary bg-primary px-4 py-2.5 text-label font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-70"
            >
              {superApplyBusy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Zap className="h-4 w-4" />
              )}
              {superApplyBusy ? 'Submitting…' : '1-Click Apply'}
              <span className="ml-1 border border-primary-foreground/40 px-1.5 py-0.5 text-overline">
                Beta
              </span>
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  if (contact && view) {
    return (
      <div
        key={view.key}
        className="flex min-h-0 flex-1 flex-col px-4 pb-4 pt-3 animate-[slideUpFade_0.25s_ease-out]"
      >
        {/* Contact identity. */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            {isReview && enableEntityActions ? (
              <button
                type="button"
                onClick={() => onViewProfile(view)}
                title="View contact"
                className="block max-w-full truncate text-left text-title-sm text-foreground underline underline-offset-2 transition-colors hover:text-primary"
              >
                {contact.fullName}
              </button>
            ) : (
              <p className="text-title-sm text-foreground">{contact.fullName}</p>
            )}
            {titleLine ? (
              <p className="mt-0.5 flex items-center gap-1 text-body-sm text-muted-foreground">
                {contact.location ? <MapPin className="h-3 w-3 shrink-0" /> : null}
                <span className="min-w-0 truncate">{titleLine}</span>
              </p>
            ) : null}
          </div>
          <div className="-mr-1 -mt-1 flex shrink-0 items-center gap-0.5">
            {decision && DECISION_BADGE[decision] ? (
              <Badge variant={DECISION_BADGE[decision]!.variant} className="mr-1 shrink-0">
                {DECISION_BADGE[decision]!.label}
              </Badge>
            ) : null}
            {/* Non-review (Simple Search): the standalone view-profile / save
                icon buttons. */}
            {!isReview && (
              <>
                <button
                  type="button"
                  onClick={() => onViewProfile(view)}
                  aria-label="View profile"
                  title="View profile"
                  className="inline-flex h-8 w-8 items-center justify-center border border-transparent text-muted-foreground transition-colors hover:border-border hover:bg-muted hover:text-foreground"
                >
                  <Eye className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => onSave(view)}
                  disabled={!paywallMode && (saveInLibrary || saveAdding)}
                  aria-label={saveInLibrary ? 'Saved to library' : 'Save to library'}
                  title={saveInLibrary ? 'In library' : 'Save to library'}
                  className="inline-flex h-8 w-8 items-center justify-center border border-transparent text-muted-foreground transition-colors hover:border-border hover:bg-muted hover:text-foreground disabled:cursor-default disabled:opacity-100 disabled:hover:bg-transparent"
                >
                  {saveAdding ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : saveInLibrary ? (
                    <BookmarkCheck className="h-4 w-4 text-primary" />
                  ) : (
                    <Bookmark className="h-4 w-4" />
                  )}
                </button>
              </>
            )}
            {/* Review (Home outreach queue): a ⋮ menu with View / Save contact.
                Only shown where real handlers exist (enableEntityActions). */}
            {isReview && enableEntityActions ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label="Contact actions"
                    title="More"
                    className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => onViewProfile(view)}>
                    <Eye />
                    View contact
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={saveInLibrary || saveAdding}
                    onSelect={() => onSave(view)}
                  >
                    {saveAdding ? (
                      <Loader2 className="animate-spin" />
                    ) : saveInLibrary ? (
                      <BookmarkCheck className="text-primary" />
                    ) : (
                      <Bookmark />
                    )}
                    {saveInLibrary ? 'Saved to library' : 'Save contact'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
        </div>

        {/* Email row — locked behind paywall for non-premium. Hidden in the
            review variant, where the recipient (with validation) lives in the
            "To" dropdown of the Generated Outreach panel instead. */}
        {!isReview && (
          <div className="mt-1.5">
            {paywallMode ? (
              <button
                type="button"
                onClick={onPaywallTrigger}
                className="inline-flex items-center gap-1 text-body-sm text-primary underline decoration-dotted hover:text-primary/80"
              >
                <Lock className="h-3 w-3" />
                Reveal email
              </button>
            ) : contact.email ? (
              <span className="inline-flex min-w-0 items-center gap-1 text-body-sm text-muted-foreground">
                <Mail className="h-3 w-3 shrink-0" />
                <span className="min-w-0 truncate">{contact.email}</span>
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-body-sm text-muted-foreground">
                <Mail className="h-3 w-3 shrink-0" />
                No email on file
              </span>
            )}
          </div>
        )}

        {/* Message panel. The 'review' variant (Automations) frames the draft
            as a document to approve; the default is the compact preview. */}
        {isReview && message.status === 'ready' && !showGeneratePlaceholder ? (
          <div className="mt-3 mb-1.5 flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1.5 text-overline tracking-[0.12em] text-muted-foreground">
              <Sparkles className="h-3 w-3 text-primary" />
              Generated outreach
            </span>
            {decision !== 'sent' ? (
              <div className="flex items-center gap-1">
                {onRefreshMessage ? (
                  <button
                    type="button"
                    onClick={() => onRefreshMessage(view)}
                    aria-label="Rebuild draft"
                    title="Rebuild this draft"
                    className="inline-flex items-center gap-1 text-caption font-semibold text-muted-foreground transition-colors hover:text-primary"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    Rebuild
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => onEdit(view)}
                  aria-label="Edit draft"
                  title="Edit draft"
                  className="inline-flex items-center gap-1 text-caption font-semibold text-muted-foreground transition-colors hover:text-primary"
                >
                  <PenLine className="h-3.5 w-3.5" />
                  Edit
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
        <div
          className={cn(
            'flex min-h-0 flex-1 flex-col border border-border bg-background/40',
            isReview ? 'p-2.5' : 'p-3 mt-3',
          )}
        >
          {showGeneratePlaceholder ? (
            // Empty past-decision draft: offer on-demand generation. Once generated,
            // the message becomes a real body and the composer below renders instead.
            <div className="flex flex-1 flex-col items-center justify-center gap-3 px-2 text-center">
              <p className="text-body-sm text-muted-foreground">
                No outreach generated for this contact yet.
              </p>
              <button
                type="button"
                onClick={() => view && onGenerate?.(view)}
                className="inline-flex items-center gap-2 border border-primary bg-primary px-4 py-2 text-label font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
              >
                <Sparkles className="h-4 w-4" />
                Generate outreach
              </button>
            </div>
          ) : message.status === 'ready' ? (
            isReview ? (
              // Compact, composer-style read-only preview: a "To" row with live
              // deliverability, the subject, and the plain message body. Clicking
              // the subject or body opens the full editor (pencil in the header).
              <div className="flex min-h-0 flex-1 flex-col gap-3">
                {validRecipientOptions.length > 0 ? (
                  <div className="flex items-center gap-2 border-b border-border/50 pb-3">
                    <span className="shrink-0 text-caption font-medium text-muted-foreground">To</span>
                    <div className="min-w-0 flex-1">
                      {revealRecipient ? (
                        <Select
                          value={resolvedSelectedRecipient}
                          onValueChange={setSelectedRecipient}
                          disabled={decision === 'sent' || combinedRecipientOptions.length <= 1}
                        >
                          <SelectTrigger
                            aria-label="Recipient"
                            className="h-8 w-full min-w-0 border-0 bg-muted/30 px-2.5 text-label font-medium shadow-none focus:ring-1 focus:ring-ring"
                          >
                            {/* Collapsed value: recipient identity ONLY (no deliverability
                                word) — the status lives at the trailing pill instead. */}
                            {isSelectedRecipientLinkedIn ? (
                              <span className="inline-flex min-w-0 items-center gap-1.5 truncate">
                                <Linkedin className="h-3.5 w-3.5 shrink-0" />
                                <span className="truncate">{linkedInSlug || resolvedSelectedRecipient}</span>
                              </span>
                            ) : (
                              <span className="truncate">{resolvedSelectedRecipient}</span>
                            )}
                          </SelectTrigger>
                          <SelectContent className="border max-h-[320px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent [scrollbar-gutter:stable]">
                            {/* LinkedIn is pinned to the TOP, set off from the email list
                                by a separator, so the alternate channel reads first. */}
                            {linkedInRecipientValue && (
                              <>
                                <SelectItem
                                  key={linkedInRecipientValue}
                                  value={linkedInRecipientValue}
                                  className="group text-body"
                                >
                                  <div className="flex w-full min-w-0 items-center justify-between gap-2">
                                    <span className="inline-flex min-w-0 items-center gap-1.5">
                                      <Linkedin className="h-3.5 w-3.5 shrink-0" />
                                      <span className="truncate">{linkedInSlug || linkedInRecipientValue}</span>
                                    </span>
                                    <span className="shrink-0 inline-flex items-center gap-1 text-caption text-muted-foreground group-data-[highlighted]:text-foreground group-data-[state=checked]:text-primary-foreground">
                                      LinkedIn
                                    </span>
                                  </div>
                                </SelectItem>
                                {orderedEmailRecipientOptions.length > 0 && <SelectSeparator />}
                              </>
                            )}
                            {orderedEmailRecipientOptions.map((option) => {
                              const optionStatusUi = getRecipientStatusUi(
                                getRecipientValidationStatus(option, recipientValidations),
                              );
                              const OptionStatusIcon = optionStatusUi.icon;
                              return (
                                <SelectItem key={option} value={option} className="group text-body">
                                  <div className="flex w-full min-w-0 items-center justify-between gap-2">
                                    <span className="truncate">{option}</span>
                                    <span className="shrink-0 inline-flex items-center gap-1 text-caption text-muted-foreground group-data-[highlighted]:text-foreground group-data-[state=checked]:text-primary-foreground">
                                      {OptionStatusIcon && (
                                        <OptionStatusIcon
                                          className={optionStatusUi.iconClassName.replace('animate-spin', '')}
                                        />
                                      )}
                                      {optionStatusUi.shortLabel}
                                    </span>
                                  </div>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      ) : (
                        <RecipientEmailReveal
                          options={recipientEmailOptions.filter(
                            (e): e is string => typeof e === 'string' && e.trim().length > 0,
                          )}
                          onReveal={onRevealRecipient}
                        />
                      )}
                    </div>
                    {revealRecipient && !isSelectedRecipientLinkedIn ? (
                      <div className="flex shrink-0 items-center">
                        {/* Deliverability status: icon + word together. `cursor-pointer`
                            (not the help cursor) so it reads as a button; hover OR click
                            opens the detail popover. */}
                        <InfoPopover
                          ariaLabel="Email deliverability details"
                          content={selectedRecipientStatusDescription}
                          triggerClassName="cursor-pointer gap-1.5 rounded px-1.5 py-1 hover:bg-muted/50"
                          trigger={
                            <span className="inline-flex items-center gap-1.5 text-caption font-medium">
                              {selectedRecipientValidationStatus === 'valid' ? (
                                <>
                                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                                  <span className="text-green-700">{selectedRecipientStatusUi.shortLabel}</span>
                                </>
                              ) : selectedRecipientValidationStatus === 'invalid' ? (
                                <>
                                  <XCircle className="h-4 w-4 text-red-600" />
                                  <span className="text-red-700">{selectedRecipientStatusUi.shortLabel}</span>
                                </>
                              ) : selectedRecipientValidationStatus === 'pending' ? (
                                <>
                                  <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                                  <span className="text-blue-700">{selectedRecipientStatusUi.shortLabel}</span>
                                </>
                              ) : (
                                <>
                                  <Info className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-muted-foreground">{selectedRecipientStatusUi.shortLabel}</span>
                                </>
                              )}
                            </span>
                          }
                        />
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {/* Subject — label above, value below (mirrors Message). The
                    value is the same size as the message body, just bolder. */}
                <button
                  type="button"
                  onClick={() => onEdit(view)}
                  className="-mx-1 block rounded px-1 text-left transition-colors hover:bg-muted/40"
                >
                  <span className="mb-0.5 block text-overline text-muted-foreground">Subject</span>
                  {/* Plain template string (not cn/twMerge): twMerge doesn't know
                      the custom `text-body-sm` utility and would drop it as a
                      conflicting `text-*` next to the colour class, blowing the
                      subject back up to the default size. */}
                  <p
                    className={`truncate text-body-sm font-semibold ${
                      message.subject ? 'text-foreground' : 'text-muted-foreground'
                    }`}
                  >
                    {message.subject || 'No subject'}
                  </p>
                </button>

                {/* Message — the label stays fixed; only the body scrolls (so the
                    "Message" heading never scrolls out of view / gets clipped). */}
                <div className="flex min-h-0 flex-1 flex-col">
                  <span className="mb-0.5 block shrink-0 text-overline text-muted-foreground">
                    Message
                  </span>
                  {/* pan-y only: lets the card's horizontal swipe-drag start here
                      instead of being swallowed by this vertical scroll area. */}
                  <div
                    className="-mx-1 min-h-0 flex-1 overflow-y-auto rounded px-1 transition-colors hover:bg-muted/30"
                    style={{ touchAction: 'pan-y', overscrollBehavior: 'contain' }}
                    role="button"
                    tabIndex={0}
                    onClick={() => onEdit(view)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') onEdit(view);
                    }}
                  >
                    <p className="whitespace-pre-wrap text-body-sm text-muted-foreground">
                      {message.body}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex min-h-0 flex-1 flex-col">
                <div className="mb-1 flex items-start justify-between gap-2">
                  {message.subject ? (
                    <p className="line-clamp-2 min-w-0 text-label text-foreground">{message.subject}</p>
                  ) : (
                    <span className="text-overline text-muted-foreground">Draft message</span>
                  )}
                  {decision !== 'sent' ? (
                    <button
                      type="button"
                      onClick={() => onEdit(view)}
                      aria-label="Edit message"
                      title="Edit message"
                      className="-mr-1 -mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center border border-transparent text-muted-foreground transition-colors hover:border-border hover:bg-muted hover:text-foreground"
                    >
                      <PenLine className="h-3.5 w-3.5" />
                    </button>
                  ) : null}
                </div>
                {/* pan-y only: lets the card's horizontal swipe-drag start here
                    instead of being swallowed by this vertical scroll area. */}
                <div
                  className="min-h-0 flex-1 overflow-y-auto"
                  style={{ touchAction: 'pan-y', overscrollBehavior: 'contain' }}
                >
                  <p className="whitespace-pre-wrap text-body-sm text-muted-foreground">{message.body}</p>
                </div>
              </div>
            )
          ) : message.status === 'error' ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center">
              <p className="text-body-sm text-muted-foreground">
                {message.error || 'Could not draft a message.'}
              </p>
              <button
                type="button"
                onClick={() => onRetryMessage(view)}
                className="inline-flex items-center gap-1.5 border border-border bg-background px-2.5 py-1.5 text-overline text-foreground transition-colors hover:bg-muted"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Retry
              </button>
            </div>
          ) : (
            <div className="flex flex-1 items-start justify-center overflow-y-auto p-3">
              {/*
                The agent takes far longer than the old single call, so a card
                backed by a streaming run shows what it is actually deciding
                rather than a caption that cannot distinguish thinking from hung.

                But ONLY when we genuinely have steps. Not every producer of a
                'generating' message streams — HomeOutreachQueue's preview
                generate and its sent-content hydration both set the status with
                no steps. Synthesising an all-PENDING plan for those renders a
                checklist that can never move, which reads as a hang and is worse
                than the honest spinner. Absent steps ⇒ fall back.
              */}
              {message.steps && message.steps.length > 0 ? (
                <OutreachAgentProgress
                  steps={message.steps}
                  trace={message.trace}
                  status="generating"
                  className="w-full bg-background"
                />
              ) : (
                <div className="flex flex-1 items-center justify-center self-center">
                  <MainLoader
                    size="sm"
                    title="Writing your message…"
                    subtitle="Personalizing the outreach"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Attached resume section — shows a tailored resume link or a tailor button. */}
        {onTailorResume && view && view.job && (
          <div className="mt-2 flex items-center gap-2 border-t border-border/50 pt-2">
            <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            {tailoringInProgress ? (
              <span className="inline-flex items-center gap-1.5 text-caption text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                Tailoring resume…
              </span>
            ) : tailoredResume ? (
              <button
                type="button"
                onClick={() =>
                  window.open(`/d/pitch/resumes/builder/${tailoredResume.resumeId}`, '_blank')
                }
                className="min-w-0 truncate text-caption font-medium text-primary underline underline-offset-2 transition-colors hover:text-primary/80"
              >
                {tailoredResume.resumeName}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => onTailorResume(view)}
                className="text-caption font-medium text-primary underline underline-offset-2 transition-colors hover:text-primary/80"
              >
                Tailor resume
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-1 items-center justify-center px-4">
      <p className="text-body-sm text-muted-foreground">No contacts for this role.</p>
    </div>
  );
}
